import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import AdjustInvPriceModal from "../../views/inventory/subtables/AdjustInvPriceModal";
import ManufacturedItemInfo from "../../components/forms/manufacturedProducts/ManufacturedItemInfo";
import { set } from "date-fns";

export function InventoryViewModel(logout) {
  const defaultFilters = {
    warehouse_location: { value: -1, label: "All locations" },
    product_data: { value: -1, label: "All PLUs" },
    class: { value: -1, label: "All Classes" },
    category: { value: -1, label: "All categories" },
  };
  const [filters, setFilters] = useState(defaultFilters);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [pluOptions, setpluOptions] = useState(false);
  const [classTypeOptions, setClassTypeOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouseLocations, setWarehouseLocations] = useState(false);
  const [rowDetails, setRowDetails] = useState({}); // { [rowId]: detailData }
  const [loadingRows, setLoadingRows] = useState({}); // { [rowId]: boolean }
  const [subRowDetails, setSubRowDetails] = useState({}); // { [rowId]: detailData }
  const [subLoadingRows, setSubLoadingRows] = useState({}); // { [rowId]: boolean }
  const [selectedItems, setSelectedItems] = useState([]);
  const [adjustInProgress, setAdjustInProgress] = useState(false);
  const [lastRow, setLastRow] = useState(null);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const getInventory = async function (search, page, filter) {
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };
    let callResult = await makePostApiCall(`getInventory`, payload);
    if (callResult.success) {
      const response = callResult.data;
      if (page > 1) {
        setInventoryData([...inventoryData, ...response.data]);
      } else {
        setLoading(false);
        setInventoryData(response.data);
        setTotalProducts(response.total);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getAllPlus = async function () {
    let callResult = await makeGetApiCall(`productsForDropdown`);
    if (callResult.success) {
      setpluOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getWarehouseLocations = async function () {
    let callResult = await makeGetApiCall(`warehouseLocationsForDropdown/2`);
    if (callResult.success) {
      setWarehouseLocations(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getClassTypes = async function () {
    setLoading(true);
    let callResult = await makeGetApiCall("classesForDropdown");
    setLoading(false);
    if (callResult.success) {
      setClassTypeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch class types");
    }
  };

  const getCategoryTypes = async function () {
    let callResult = await makeGetApiCall("categoriesForDropdown");
    if (callResult.success) {
      setCategories([
        { value: -1, label: "All categories" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch category types");
    }
  };

  const filterComponents = [
    <FilterSingleSelectDropdown
      header="PLU"
      onChange={(value) =>
        setFilters({
          ...filters,
          product_data: value,
        })
      }
      options={pluOptions}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="plu"
      value={filters.product_data}
    />,
    <FilterSingleSelectDropdown
      header="Class"
      onChange={(value) =>
        setFilters({
          ...filters,
          class: value,
        })
      }
      options={classTypeOptions}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="class"
      value={filters.class}
    />,
    <FilterSingleSelectDropdown
      header="Category"
      onChange={(value) =>
        setFilters({
          ...filters,
          category: value,
        })
      }
      options={categories}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="category"
      value={filters.category}
    />,
    <FilterSingleSelectDropdown
      header="Location"
      onChange={(value) =>
        setFilters({
          ...filters,
          warehouse_location: value,
        })
      }
      options={warehouseLocations}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="warehouse_location"
      value={filters.warehouse_location}
    />,
  ];

  const resetFilters = () => {
    setFilters(defaultFilters);
    setShowFilters(false);
    getInventory(null, 1, defaultFilters);
  };

  const printInventorySummaryReport = async function (search, filter) {
    const payload = {
      search: search,
      filter: filter,
    };
    let callResult = await makePostApiCall(
      `printInventorySummaryReport`,
      payload
    );
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const openSlideForManufacturedItemDetails = async function (
    manProdId,
    manProdPalletId
  ) {
    var callResult = { success: false };
    if (manProdPalletId) {
      callResult = await makeGetApiCall(
        `manufacturedPallets/${manProdPalletId}`
      );
    } else {
      callResult = await makeGetApiCall(`manufacturedProducts/${manProdId}`);
    }
    if (callResult.success) {
      setSlideOver({
        childComponent: (
          <ManufacturedItemInfo item={callResult.data} setOpen={setSlideOver} />
        ),
        open: true,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch manufactured item");
    }
  };

  const printSubItems = async function (item) {
    let callResult = await makePostApiCall(`printSubItemsForInventory`, item);
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchRowDetails = async (rowData) => {
    setLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makePostApiCall(`inventory/plu/location`, {
      plu: rowData.plu,
      warehouse_location_id: rowData.warehouse_location_id,
    });
    if (callResult.success) {
      setRowDetails({ ...rowDetails, [rowData.unique_id]: callResult.data });
      setLoadingRows({ ...loadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchSubRowDetails = async (rowData) => {
    setLastRow(rowData);
    setSubLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makePostApiCall(`inventory/plu/location/lotNumber`, {
      plu: rowData.plu,
      lot_number: rowData.lot_number,
      warehouse_location_id: rowData.warehouse_location_id,
    });
    if (callResult.success) {
      setSubRowDetails({
        ...subRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setSubLoadingRows({ ...subLoadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const onAdjustPrice = async (cost) => {
    let callResult = await makePostApiCall(`inventory/adjustPrice`, {
      cost: cost,
      items: selectedItems,
    });
    if (callResult.success) {
      fetchSubRowDetails(lastRow);
      setSelectedItems([]);
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    return callResult;
  };

  function openAdjustPriceModal() {
    setEmptyModelOpen({
      childComponent: (
        <AdjustInvPriceModal
          setEmptyModelOpen={setEmptyModelOpen}
          onAdjustPrice={onAdjustPrice}
        />
      ),
      open: true,
    });
  }

  return {
    emptyModal,
    setEmptyModelOpen,
    getInventory,
    loading,
    inventoryData,
    showFilters,
    setShowFilters,
    filters,
    filterComponents,
    resetFilters,
    getWarehouseLocations,
    getAllPlus,
    getClassTypes,
    getCategoryTypes,
    printInventorySummaryReport,
    printSubItems,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    selectedItems,
    setSelectedItems,
    openAdjustPriceModal,
    adjustInProgress,
    totalProducts,
    slideOver,
    setSlideOver,
    openSlideForManufacturedItemDetails,
  };
}
